import React, { useMemo, useCallback } from 'react';
import { useGetAllActivityItemsQuery, useGetAllActivityItemsWithCustomFieldsQuery, useGetAllActivityTypesQuery } from './activitySlice';
import { Grid } from '@material-ui/core';
import CustomInput from 'components/CustomInput';
import { arrayToObject } from 'utils/object-util';
import { useGetAllBundlesQuery } from 'features/activityBundle/activityBundleSlice';

const ActivityTypesField = ({ required = true, onChange, value, label = "Activity Type", readonly = false, includeBundles = false, customFields = false, styling }) => {
    
    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery({skip: customFields}); 
    let { data: activityItemsWithCustomFields, error: activityItemError, isLoading: isLoadingActivityItemsWithCustomFields } = useGetAllActivityItemsWithCustomFieldsQuery({skip: !customFields});
    let { data: activityTypes, error: activityTypesError, isFetching: isLoadingActivityTypes } = useGetAllActivityTypesQuery();
    let { data: bundleItems, error: bundlesError, isFetching: isLoadingBundles } = useGetAllBundlesQuery(undefined, { skip: !includeBundles });

    let activityItemsToUse = customFields ? activityItemsWithCustomFields : activityItems;

    const handleChange = useCallback((val) => {
        if (!includeBundles) {
            onChange(val);
            return;
        }
        if (!val) {
            onChange(null, { isBundle: false, jsonValue: null });
            return;
        }
        const parsed = JSON.parse(val);
        const id = parsed.isBundle ? parsed.bundleId : parsed.activityItemId;
        onChange(id, { isBundle: parsed.isBundle, jsonValue: val });
    }, [onChange, includeBundles]);

    const combinedData = useMemo(() => {
        if (!activityItemsToUse || !activityTypes) return [];
        const sortedActivityTypes = [...activityTypes]?.sort((a, b) => a.name.localeCompare(b.name));
        const sortedActivityItems = [...activityItemsToUse]?.sort((a, b) => a.name.localeCompare(b.name));
        const sortedBundleItems = includeBundles
            ? [...(bundleItems || [])].sort((a, b) => a.name.localeCompare(b.name))
            : [];
        const activities = sortedActivityTypes?.map(type => ({
            label: type.name,
            options: sortedActivityItems
                .filter(item => item.activityTypeId === type.id)
                .map(item => ({ 
                    label: item.name, 
                    value: includeBundles 
                        ? { activityItemId: item.id, bundleId: null, activityTypeId: item.activityTypeId, isBundle: false } 
                        : item.id 
                }))
        }));
        if (includeBundles) {
            activities?.push({
                label: 'Bundles',
                options: sortedBundleItems.map(item => ({ 
                    label: item.name, 
                    value: { activityItemId: null, bundleId: item.id, activityTypeId: null, isBundle: true } 
                }))
            });
        }

        return [...activities];
    }, [activityTypes, activityItemsToUse, bundleItems, includeBundles]);

    let activityItemsLookup = arrayToObject(activityItemsToUse);

    return (
        <>
            {readonly ? <label>{activityItemsLookup?.[value]?.name}</label> :
                <Grid>
                    <CustomInput
                        id="activityTypeId"
                        label={label}
                        elementType="dropdown"
                        combinedData={combinedData}
                        value={value}
                        required={required}
                        showEmpty={true}
                        onChange={handleChange}
                        classStyles={styling}
                    />
                </Grid>}
        </>
    );
};

export default ActivityTypesField;